import { ATTACK_ONGOING, MAX_FEED_LOSSES } from './feed';
import type { Notification, NotificationKind } from './types';

/**
 * The right-hand alert stack: entries grouped into tiers (attacks pinned on
 * top, then warnings, then good news as full cards, then folded info),
 * filter-chip counts and per-kind mutes. ALERT_KINDS is the one place a kind's icon, tier, filter group and
 * mutability are set: a new kind is a row here plus its deriver.
 */

/** Stack sections, top to bottom: an attack outranks a warning outranks good news outranks info. */
export const ALERT_TIERS = ['attack', 'warning', 'news', 'info'] as const;
export type AlertTier = (typeof ALERT_TIERS)[number];

/** Filter chips, in order; a new group is added here with its first kind. */
export const ALERT_GROUPS = [
  { id: 'combat', icon: '⚔', label: 'Combat' },
  { id: 'workers', icon: '👷', label: 'Workers' },
  { id: 'economy', icon: '🌾', label: 'Economy' },
  { id: 'upgrades', icon: '⬆', label: 'Upgrades' },
  { id: 'info', icon: '✦', label: 'Info' },
] as const;
export type AlertGroup = (typeof ALERT_GROUPS)[number]['id'];

export interface KindMeta {
  icon: string;
  tier: AlertTier;
  group: AlertGroup;
  /** Name in the mute list. */
  label: string;
  /** Can be muted. Attacks and losses never can: missing one costs the player a city. */
  mutable: boolean;
  /** Border and surface classes of its entry (design-system tokens). */
  style: string;
  /** Shows a ×N chip (see countChip) instead of a number in the text. */
  countChip: boolean;
}

export const ALERT_KINDS: Record<NotificationKind, KindMeta> = {
  attack: {
    icon: '⚔️',
    tier: 'attack',
    group: 'combat',
    label: 'Attacks',
    mutable: false,
    style: 'border-danger/40 bg-danger-surface',
    countChip: true,
  },
  loss: {
    icon: '🔥',
    tier: 'warning',
    group: 'combat',
    label: 'Losses',
    mutable: false,
    style: 'border-danger/40 bg-danger-surface',
    countChip: false,
  },
  spy: {
    icon: '🕵️',
    tier: 'warning',
    group: 'combat',
    label: 'Spies',
    mutable: true,
    style: 'border-warn/40',
    countChip: false,
  },
  idle: {
    icon: '💤',
    tier: 'warning',
    group: 'workers',
    label: 'Idle workers',
    mutable: true,
    style: 'border-warn/40',
    countChip: true,
  },
  food: {
    icon: '🌾',
    tier: 'warning',
    group: 'economy',
    label: 'Food running out',
    mutable: true,
    style: 'border-warn/40',
    countChip: false,
  },
  cap: {
    icon: '👥',
    tier: 'warning',
    group: 'economy',
    label: 'Unit caps',
    mutable: true,
    style: 'border-warn/40',
    countChip: false,
  },
  upgradePaused: {
    icon: '⏸',
    tier: 'warning',
    group: 'upgrades',
    label: 'Upgrades paused',
    mutable: true,
    style: 'border-danger/40 bg-danger-surface',
    countChip: false,
  },
  upgraded: {
    icon: '🛠️',
    tier: 'news',
    group: 'upgrades',
    label: 'Buildings upgraded',
    mutable: true,
    style: 'border-ok/40',
    countChip: false,
  },
  era: {
    icon: '⬆️',
    tier: 'news',
    group: 'upgrades',
    label: 'City eras',
    mutable: true,
    style: 'border-ok/40',
    countChip: false,
  },
  trained: {
    icon: '🎖️',
    tier: 'info',
    group: 'info',
    label: 'Units trained',
    mutable: true,
    style: 'border-ok/40',
    countChip: true,
  },
  arrival: {
    icon: '📍',
    tier: 'info',
    group: 'info',
    label: 'Arrivals',
    mutable: true,
    style: '',
    countChip: false,
  },
  plunder: {
    icon: '💰',
    tier: 'info',
    group: 'info',
    label: 'Plunder',
    mutable: true,
    style: 'border-ok/40',
    countChip: true,
  },
  discovery: {
    icon: '👁️',
    tier: 'info',
    group: 'info',
    label: 'Rivals found',
    mutable: true,
    style: 'border-warn/40',
    countChip: false,
  },
};

/**
 * "×3" for an entry that stands for several things: attack hits merged, units
 * trained, workers idle (the text carries no number). Null for one, or for
 * kinds without a chip.
 */
export function countChip(n: Notification): string | null {
  if (!ALERT_KINDS[n.kind].countChip) return null;
  const total =
    n.kind === 'trained'
      ? Object.values(n.trained?.counts ?? {}).reduce((sum, k) => sum + k, 0)
      : n.kind === 'idle'
        ? (n.idle ?? 1)
        : n.count;
  return total > 1 ? `×${total}` : null;
}

/** The kinds a stored mute list holds that may still be muted (unknown or unmutable ones dropped). */
export function mutedKinds(stored: unknown): Set<NotificationKind> {
  const out = new Set<NotificationKind>();
  if (!Array.isArray(stored)) return out;
  for (const k of stored) {
    if (typeof k === 'string' && k in ALERT_KINDS && ALERT_KINDS[k as NotificationKind].mutable)
      out.add(k as NotificationKind);
  }
  return out;
}

/** The mute list with `kind` switched; unmutable kinds are left as they are. */
export function toggleMuted(
  muted: readonly NotificationKind[],
  kind: NotificationKind
): NotificationKind[] {
  if (!ALERT_KINDS[kind].mutable) return [...muted];
  return muted.includes(kind) ? muted.filter((k) => k !== kind) : [...muted, kind];
}

export interface StackEntry {
  n: Notification;
  /**
   * An open attack: on top and not dismissable for its whole life, until it
   * expires ATTACK_LINGER s after the last hit (the raiders left or died).
   */
  pinned: boolean;
  /** Hit within ATTACK_ONGOING s: it pulses. */
  ongoing: boolean;
}

/** The notifications not muted (unmutable kinds never are). */
export function unmuted(
  list: readonly Notification[],
  muted: ReadonlySet<NotificationKind>
): Notification[] {
  return list.filter((n) => !(muted.has(n.kind) && ALERT_KINDS[n.kind].mutable));
}

export interface StackView {
  /** The filter in effect: the chosen group while it has entries, else null (a chip that went away can't stick). */
  group: AlertGroup | null;
  /** Non-empty tiers in ALERT_TIERS order, entries newest first. */
  tiers: { tier: AlertTier; entries: StackEntry[] }[];
  /** Shown entries per filter group, before the group filter and the loss folding. */
  counts: Record<AlertGroup, number>;
  /** All shown entries (the "All N" chip). */
  total: number;
  /** Losses folded into "+N older losses". */
  olderLosses: number;
}

/**
 * The stack at sim time `now`: undismissed, unmuted entries (muted kinds
 * don't count either) in tiers, newest first by id within a tier so a row
 * updated in place keeps its place. `group` narrows the listing to one filter
 * chip (ignored once it has no entries); only the MAX_FEED_LOSSES newest losses are listed unless `allLosses`.
 */
export function stackView(
  list: readonly Notification[],
  now: number,
  muted: ReadonlySet<NotificationKind>,
  opts: { group?: AlertGroup; allLosses?: boolean } = {}
): StackView {
  const counts: Record<AlertGroup, number> = {
    combat: 0,
    workers: 0,
    economy: 0,
    upgrades: 0,
    info: 0,
  };
  const shown = unmuted(list, muted)
    .filter((n) => !n.dismissed)
    .sort((a, b) => b.id - a.id);
  for (const n of shown) counts[ALERT_KINDS[n.kind].group]++;
  const group = opts.group && counts[opts.group] > 0 ? opts.group : null;
  const inGroup = group ? shown.filter((n) => ALERT_KINDS[n.kind].group === group) : shown;
  let losses = 0;
  const listed = opts.allLosses
    ? inGroup
    : inGroup.filter((n) => n.kind !== 'loss' || ++losses <= MAX_FEED_LOSSES);
  const tiers = ALERT_TIERS.map((tier) => ({
    tier,
    entries: listed
      .filter((n) => ALERT_KINDS[n.kind].tier === tier)
      .map((n) => ({
        n,
        pinned: n.kind === 'attack',
        ongoing: n.kind === 'attack' && now - n.lastTime <= ATTACK_ONGOING,
      })),
  })).filter((t) => t.entries.length > 0);
  return {
    group,
    tiers,
    counts,
    total: shown.length,
    olderLosses: opts.allLosses ? 0 : Math.max(0, losses - MAX_FEED_LOSSES),
  };
}
